import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { ShieldCheck, Check, X, Trash2, ArrowLeft, MessageSquareWarning } from "lucide-react";

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

export default function AdminCommunityModeration() {
  const [pending, setPending] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/community/moderation");
      setPending(data.pending || []);
      setRejected(data.rejected || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const act = async (postId, action) => {
    setBusy(postId + action);
    try {
      if (action === "approve") await api.post(`/community/posts/${postId}/approve`);
      else if (action === "reject") await api.post(`/community/posts/${postId}/reject`);
      else if (action === "delete") await api.delete(`/community/posts/${postId}`);
      toast.success(action === "approve" ? "Approved" : action === "reject" ? "Rejected" : "Deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(null); }
  };

  const renderCard = (p, showRejectionReason) => (
    <li key={p.id} data-testid={`mod-post-${p.id}`} className="p-5 bg-white rounded-3xl card-tactile">
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${p.author_role === "admin" ? "bg-sky text-white" : "bg-canvas text-stone"}`}>
            {p.author_role === "admin" ? "Teacher" : "Student"}
          </span>
          <span className="text-sm font-semibold text-ink">{p.author_name}</span>
          <span className="text-xs text-pebble">·</span>
          <span className="text-xs text-pebble">{fmt(p.created_at)}</span>
          {p.is_reply && (
            <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-lemon/40 text-ink font-bold">
              Reply on: {p.thread_title || "(deleted thread)"}
            </span>
          )}
        </div>
      </div>
      {!p.is_reply && <h3 className="font-heading text-lg font-bold text-ink mb-1">{p.title}</h3>}
      <p className="text-ink whitespace-pre-wrap leading-relaxed">{p.body}</p>
      {showRejectionReason && p.moderator_note && (
        <div className="mt-3 text-xs text-watermelon-hover bg-watermelon/10 rounded-2xl px-3 py-2 font-semibold">
          {p.moderator_note}
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-4">
        {!showRejectionReason && (
          <button
            data-testid={`approve-${p.id}`}
            onClick={() => act(p.id, "approve")}
            disabled={!!busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-leaf text-white font-bold text-sm btn-tactile hover:brightness-95 disabled:opacity-70"
          >
            <Check className="w-4 h-4" /> Approve
          </button>
        )}
        {!showRejectionReason && (
          <button
            data-testid={`reject-${p.id}`}
            onClick={() => act(p.id, "reject")}
            disabled={!!busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-canvas text-ink font-bold text-sm btn-tactile hover:brightness-95 disabled:opacity-70"
          >
            <X className="w-4 h-4" /> Reject
          </button>
        )}
        <button
          data-testid={`delete-${p.id}`}
          onClick={() => act(p.id, "delete")}
          disabled={!!busy}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-watermelon text-white font-bold text-sm btn-tactile hover:bg-watermelon-hover disabled:opacity-70"
        >
          <Trash2 className="w-4 h-4" /> Delete
        </button>
      </div>
    </li>
  );

  return (
    <AppLayout variant="admin">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link to="/admin/community" data-testid="back-to-community" className="inline-flex items-center gap-2 text-sky-hover font-bold text-sm mb-4 hover:underline">
          <ArrowLeft className="w-4 h-4" /> Back to community
        </Link>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-lemon/40 text-ink font-bold text-sm mb-3">
          <ShieldCheck className="w-4 h-4" /> Moderation
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-ink">Review posts</h1>
        <p className="text-lg text-stone mt-2 mb-8">Approve student posts before they go live, or reject inappropriate content.</p>

        <section className="mb-10">
          <h2 className="font-heading text-2xl font-bold text-ink mb-4">
            Pending <span className="text-stone font-semibold">({pending.length})</span>
          </h2>
          {loading ? (
            <div className="text-stone" data-testid="mod-loading">Loading…</div>
          ) : pending.length === 0 ? (
            <div data-testid="pending-empty" className="p-8 bg-white rounded-3xl card-tactile text-center text-stone">
              Nothing waiting for review. Great job keeping up!
            </div>
          ) : (
            <ul data-testid="pending-list" className="space-y-3">
              {pending.map((p) => renderCard(p, false))}
            </ul>
          )}
        </section>

        <section>
          <h2 className="font-heading text-2xl font-bold text-ink mb-4 flex items-center gap-2">
            <MessageSquareWarning className="w-6 h-6 text-watermelon" />
            Auto-blocked <span className="text-stone font-semibold">({rejected.length})</span>
          </h2>
          {rejected.length === 0 ? (
            <div className="p-6 bg-white rounded-3xl card-tactile text-center text-stone">No blocked posts.</div>
          ) : (
            <ul data-testid="rejected-list" className="space-y-3">
              {rejected.map((p) => renderCard(p, true))}
            </ul>
          )}
        </section>
      </div>
    </AppLayout>
  );
}
